/** @jsxImportSource @emotion/react */
import { Tag, Typography } from '@quickbit/qb-design-system';
import { Link } from 'react-router-dom';
import QbPayFlow from '../../images/qb-pay-flow.png';
import { CodeBlock } from '../CodeBlock';
import { InlineCode } from '../InlineCode';
import { MagnifiedImage } from '../MagnifiedImage';
import { PageText } from './types';

const apiKeyExample = `{
    ...
    "Content-Type": "application/json",
    "X-Api-Key": "IpnY9w96eWqiWwY9jCbt3y7cC49SOTHre7nYpIIoAoSVQ-V-17E-yWjjCPXiOT0Z",
    ...
}`;

const createOrderExample = `{
    "amount": 100,
    "currency": "EUR",
    "callback_url": "https://my-domain.com/api/quickbit-callback",
    "redirect_url": "https://my-domain.com/api/quickbit-redirect"
}`;

const createOrderResponseExample = `{
    "amount": 100,
    "crypto_amount": null,
    "currency": "EUR",
    "uuid": "3fa85f64-5717-4562-b3fc-2c963f66afa6",
    "created_at": "2023-04-12T06:23:07.558Z",
    "checkout_url": "https://qb-pay.quickbit.com?order-id=3fa85f64-5717-4562-b3fc-2c963f66afa6",
    "reference_id": null,
    "status": "PENDING",
    "creation_amount": 100,
    "errors": null,
    "email": null
}`;

export const integrationDocumentation: Array<PageText> = [
  {
    id: 'integrating-with-quickbit-pay',
    title: 'Integrating with Quickbit Pay',
    page: 'integration',
    content: (
      <Typography variant="heading1">Integrating with Quickbit Pay</Typography>
    ),
  },
  {
    id: 'integrating-with-quickbit-pay-1',
    title: 'Integrating with Quickbit Pay',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          This page will go through the different steps required to integrate
          with the Quickbit Pay product. This includes describing the flow for
          completing a customer&apos;s purchase, as well as how the endpoints
          from the Quickbit Pay API should be used.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'integrating-with-quickbit-pay-2',
    title: 'Integrating with Quickbit Pay',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          For a detailed specification of the API endpoints, see{' '}
          <Link to="/docs/endpoints">Endpoints</Link>.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-pay-checkout-flow',
    title: 'The Quickbit Pay checkout flow',
    page: 'integration',
    content: (
      <Typography variant="heading2">The Quickbit Pay checkout flow</Typography>
    ),
  },
  {
    id: 'the-quickbit-pay-checkout-flow-1',
    title: 'The Quickbit Pay checkout flow',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          Quickbit Pay is responsible for the payment part of your
          website&apos;s checkout flow. This means that your website is
          responsible for making sure the correct items are being purchased.
          After initiating the Quickbit Pay checkout flow, we will take over and
          make sure the correct amount of money is spent by the customer. We
          will then notify you about whether the order was successful or not.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-pay-checkout-flow-2',
    title: 'The Quickbit Pay checkout flow',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          Below we outline the steps of this flow.
        </Typography>
        <MagnifiedImage
          src={QbPayFlow}
          alt="The Quickbit Pay checkout page"
          width={2243}
          height={871}
        />
        <br></br>
      </>
    ),
  },
  {
    id: 'authenticating-your-requests',
    title: 'Authenticating your requests',
    page: 'integration',
    content: (
      <Typography variant="heading2">Authenticating your requests</Typography>
    ),
  },
  {
    id: 'authenticating-your-requests-1',
    title: 'Authenticating your requests',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          In order for Quickbit Pay to know which merchant is responsible for
          which order, we need to authenticate the requests sent to our system.
          This is done using API keys. When you were onboarded as a merchant,
          you should have been provided with such a key.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'authenticating-your-requests-2',
    title: 'Authenticating your requests',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          Each request sent to the Quickbit Pay system needs to provide the
          aforementioned API key in its headers. Specifically, the{' '}
          <InlineCode>X-Api-Key</InlineCode> header should be set to your API
          key. See an example below.
        </Typography>
        <CodeBlock language="json" code={apiKeyExample} />
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-pay-environments',
    title: 'The Quickbit Pay environments',
    page: 'integration',
    content: (
      <Typography variant="heading2">The Quickbit Pay environments</Typography>
    ),
  },
  {
    id: 'the-quickbit-pay-environments-1',
    title: 'The Quickbit Pay environments',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          There are two environments available for Quickbit Pay; a test
          environment and a production environment. The production environment
          should be used in your production environment, and the test
          environment should be used when developing your integration with us.
          Each environment requires a separate API key.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-pay-environments-2',
    title: 'The Quickbit Pay environments',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          The test environment differs only slightly from the production
          environment. No real settlements are performed, and it is possible to
          simulate order status changes from the Quickbit Pay backoffice. This
          simulation was added to remove the need for you to send money to test
          your integration.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-pay-environments-3',
    title: 'The Quickbit Pay environments',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          The test environment is available at{' '}
          <a href="https://qb-pay.test.quickbit.com">
            https://qb-pay.test.quickbit.com
          </a>{' '}
          and the production environment is available at{' '}
          <a href="https://qb-pay.quickbit.com">https://qb-pay.quickbit.com</a>.
          This means that, for instance, a request for creating a pay order
          (described in more detail below) can be sent to{' '}
          <a href="https://qb-pay.test.quickbit.com/api/pay/v1/orders/pay">
            https://qb-pay.test.quickbit.com/api/pay/v1/orders/pay
          </a>
          .
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'the-quickbit-pay-environments-4',
    title: 'The Quickbit Pay environments',
    page: 'integration',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          Even though the test environment does not pay out any settlements or
          require real money to be used, it still uses real blockchain addresses
          for sending crypto. This means that if you were to send crypto to an
          address generated with Quickbit Pay, real money will be sent to that
          address.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-an-order',
    title: 'Creating an order',
    page: 'integration',
    content: <Typography variant="heading2">Creating an order</Typography>,
  },
  {
    id: 'creating-an-order-1',
    title: 'Creating an order',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          To begin the Quickbit Pay checkout flow, an order must be created in
          the Quickbit Pay system to keep track of the amount required and the
          status of the payment. The endpoint used depends on the type of order
          you want to create:
        </Typography>
        <ul>
          <li>
            <Typography variant="body">
              <strong>Pay Orders</strong> - Use{' '}
              <InlineCode>POST /api/pay/v1/orders/pay</InlineCode> for standard
              cryptocurrency payment orders. See{' '}
              <Link to="/docs/integration/pay-orders">Pay Orders</Link> for
              detailed information.
            </Typography>
          </li>
          <li>
            <Typography variant="body">
              <strong>Onramp Orders</strong> - Use{' '}
              <InlineCode>POST /api/pay/v1/orders/onramp</InlineCode> for orders
              where customers purchase cryptocurrency with fiat. See{' '}
              <Link to="/docs/integration/onramp">Onramp Flow</Link> for
              detailed information.
            </Typography>
          </li>
          <li>
            <Typography variant="body">
              <strong>Processing Orders</strong> - Use{' '}
              <InlineCode>POST /api/pay/v1/order</InlineCode> for processing
              orders (legacy endpoint, still supported for processing flow).
            </Typography>
          </li>
        </ul>
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-an-order-2',
    title: 'Creating an order',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          The endpoint for creating orders requires a JSON body sent with it.
          This body should contain the amount as well as the currency of the
          order. You can optionally provide a URL where we can notify you about
          updates to the order. Finally, the body can optionally contain two
          additional parameters: a reference ID and a redirect URL. The
          reference ID can be added for you to keep track of the order in your
          system; it will be included in callbacks about the order. The redirect
          URL is a way to automatically redirect customers to a link of your
          choice once an order has been completed.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-an-order-3',
    title: 'Creating an order',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          As an example, imagine your website sells shoes and a customer wants
          to purchase two pairs of shoes for a price of 100 EUR. You would then
          create an order with the following request body:
        </Typography>
        <CodeBlock language="json" code={createOrderExample} />
        <Typography variant="body">
          You would then receive a response with the created order. This order
          will contain information about the amount (and the creation amount),
          when the order was created, its status (which will be{' '}
          <InlineCode>PENDING</InlineCode> for newly created orders), as well as
          containing a checkout URL that will be described more in the next
          section. An example of an order response can be seen below.
        </Typography>
        <CodeBlock language="json" code={createOrderResponseExample} />
        <Typography variant="body">
          The <InlineCode>uuid</InlineCode> is the unique identifier of your
          order. When notifying you about order changes, this identifier will be
          used. Make sure to save this ID in your system.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-an-order-4',
    title: 'Creating an order',
    page: 'integration',
    content: (
      <>
        <Typography variant="body">
          The difference between an order&apos;s amount and creation amount is
          dependent on Quickbit&apos;s fee configuration for your integration.
          If a customer fee is set (i.e. a fee that is paid by your customers),
          the amount will include that fee, whereas the creation amount will be
          equal to the amount entered when sending the request. The difference
          between these two amounts is therefore the customer fee for the order.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-an-order-7',
    title: 'Creating an order',
    page: 'integration',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          The <InlineCode>callback_url</InlineCode> in the request body should
          be replaced with the URL specifically created in your system.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-an-order-8',
    title: 'Creating an order',
    page: 'integration',
    content: (
      <>
        <Tag value="Note" iconName="info" variant="primary" size="large" />
        <Typography variant="body">
          Only certain currencies are supported when creating orders. See the{' '}
          <Link to="/docs/endpoints">Endpoints</Link> page for exact details.
        </Typography>
        <br></br>
      </>
    ),
  },
  {
    id: 'creating-an-order-deprecation-notice',
    title: 'Creating an order',
    page: 'integration',
    content: (
      <>
        <Tag value="Deprecated" iconName="info" variant="danger" size="large" />
        <Typography variant="body">
          The legacy <InlineCode>POST /api/pay/v1/order</InlineCode> endpoint is
          deprecated for pay orders and onramp orders. While it still works for
          backward compatibility, we recommend migrating to the new specific
          endpoints:
        </Typography>
        <ul>
          <li>
            For standard cryptocurrency payments: Use{' '}
            <Link to="/docs/integration/pay-orders">Pay Orders</Link> with{' '}
            <InlineCode>POST /api/pay/v1/orders/pay</InlineCode>
          </li>
          <li>
            For fiat-to-crypto purchases: Use{' '}
            <Link to="/docs/integration/onramp">Onramp Orders</Link> with{' '}
            <InlineCode>POST /api/pay/v1/orders/onramp</InlineCode>
          </li>
        </ul>
        <Typography variant="body">
          The legacy endpoint will continue to be supported for processing
          orders, but documentation for checkout page rendering, callbacks,
          order fetching, and other post-creation operations has been moved to
          the specific order type documentation pages.
        </Typography>
        <br></br>
      </>
    ),
  },
];
